import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CreditCard, CheckCircle2, Globe, ArrowLeft, BookOpen, Zap, CalendarCheck } from "lucide-react";

const API = "/api";

const TEXTS = {
  en: {
    title: "Choose Your Plan",
    subtitle: "Pick a monthly subscription plan and start your English learning journey.",
    perMonth: "per month",
    classesPerMonth: "classes / month",
    classesPerWeek: "classes / week",
    total: "Total",
    popular: "Most Popular",
    selectPlan: "Select Plan",
    processing: "Processing...",
    secure: "Secure payment powered by Stripe & PayMongo",
    paymongoHint: "Pay with international card, GCash, Maya, or GrabPay via PayMongo.",
    back: "Back to Dashboard",
    langSwitch: "Turkce",
    bestValue: "Best Value",
    noPlans: "No subscription plans are currently available. Please check back soon.",
  },
  tr: {
    title: "Paketinizi Secin",
    subtitle: "Aylik bir abonelik paketi secin ve Ingilizce ogrenme yolculuguna baslayin.",
    perMonth: "aylik",
    classesPerMonth: "ders / ay",
    classesPerWeek: "ders / hafta",
    total: "Toplam",
    popular: "En Populer",
    selectPlan: "Paketi Sec",
    processing: "Isleniyor...",
    secure: "Stripe ve PayMongo ile guvenli odeme",
    paymongoHint: "Uluslararasi kart, GCash, Maya veya GrabPay ile PayMongo uzerinden ode.",
    back: "Panele Don",
    langSwitch: "English",
    bestValue: "En Iyi Deger",
    noPlans: "Su anda kullanilabilir abonelik paketi bulunmuyor. Lutfen daha sonra tekrar deneyin.",
  }
};

export default function SubscriptionPage() {
  const [searchParams] = useSearchParams();
  const [lang, setLang] = useState(searchParams.get("lang") || "en");
  const t = TEXTS[lang];
  const { user } = useAuth();
  const navigate = useNavigate();

  const [plans, setPlans] = useState([]);
  const [currencySymbol, setCurrencySymbol] = useState("TL");
  const [selectedPlanId, setSelectedPlanId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [provider, setProvider] = useState(null); // 'stripe' | 'paymongo'
  const [paymongoEnabled, setPaymongoEnabled] = useState(false);
  const [fxRate, setFxRate] = useState(null);

  useEffect(() => {
    axios.get(`${API}/payments/pricing`).then(r => {
      setPlans(r.data.plans || []);
      setCurrencySymbol(r.data.currency_symbol || "TL");
    }).catch(() => toast.error("Failed to load pricing"));
    axios.get(`${API}/payments/paymongo/config`).then(r => {
      setPaymongoEnabled(!!r.data?.configured);
    }).catch(() => setPaymongoEnabled(false));
    axios.get(`${API}/payments/paymongo/fx-rate`).then(r => {
      if (r.data?.rate) setFxRate(r.data.rate);
    }).catch(() => setFxRate(null));
  }, []);

  const handleCheckout = async (plan) => {
    if (!user?.student_id) {
      toast.error("Please log in to purchase credits");
      return;
    }
    setSelectedPlanId(plan.id);
    setProvider("stripe");
    setLoading(true);
    try {
      const res = await axios.post(`${API}/payments/checkout`, {
        plan_id: plan.id,
        student_id: user.student_id,
        origin_url: window.location.origin,
      });
      if (res.data.url) window.location.href = res.data.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Checkout failed");
      setSelectedPlanId(null);
    } finally {
      setLoading(false);
      setProvider(null);
    }
  };

  const handlePaymongoCheckout = async (plan) => {
    if (!user?.student_id) {
      toast.error("Please log in to purchase credits");
      return;
    }
    setSelectedPlanId(plan.id);
    setProvider("paymongo");
    setLoading(true);
    try {
      const res = await axios.post(`${API}/payments/paymongo/checkout`, {
        plan_id: plan.id,
        student_id: user.student_id,
      });
      if (res.data.url) window.location.href = res.data.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "PayMongo checkout failed");
      setSelectedPlanId(null);
    } finally {
      setLoading(false);
      setProvider(null);
    }
  };

  // "Most Popular" = plan with the highest classes_per_month among active plans
  // (matches the historical "popular" heuristic without hard-coding indexes).
  const popularId = plans.length > 0
    ? plans.reduce((max, p) => (p.classes_per_month > (max?.classes_per_month || 0) ? p : max), null)?.id
    : null;
  // "Best Value" = lowest per-class effective rate across active plans (when >1 plan)
  const bestValueId = plans.length > 1
    ? plans.reduce((best, p) => {
        const rate = p.monthly_price / p.classes_per_month;
        return (!best || rate < best.rate) ? { id: p.id, rate } : best;
      }, null)?.id
    : null;

  return (
    <div translate="no" className="min-h-screen bg-gradient-to-br from-teal-50 via-white to-emerald-50 py-8 px-4">
      <div className="max-w-5xl mx-auto space-y-8">
        {/* Header */}
        <div className="flex items-center justify-between">
          <button onClick={() => navigate("/student-dashboard")} className="flex items-center gap-1.5 text-teal-600 hover:text-teal-800 text-sm font-medium">
            <ArrowLeft className="w-4 h-4" /> {t.back}
          </button>
          <button onClick={() => setLang(lang === "en" ? "tr" : "en")}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-teal-200 text-teal-700 hover:bg-teal-50 text-sm font-medium"
            data-testid="pricing-lang-toggle">
            <Globe className="w-4 h-4" /> {t.langSwitch}
          </button>
        </div>

        <div className="text-center space-y-3">
          <div className="w-16 h-16 bg-gradient-to-br from-teal-500 to-emerald-600 rounded-2xl flex items-center justify-center mx-auto">
            <BookOpen className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-slate-800">{t.title}</h1>
          <p className="text-slate-500 max-w-md mx-auto">{t.subtitle}</p>
        </div>

        {/* Plans Grid */}
        {plans.length === 0 ? (
          <Card className="border-slate-200">
            <CardContent className="py-12 text-center text-slate-500">{t.noPlans}</CardContent>
          </Card>
        ) : (
          <div className={`grid grid-cols-1 sm:grid-cols-2 ${plans.length >= 3 ? "lg:grid-cols-3" : ""} ${plans.length >= 4 ? "xl:grid-cols-4" : ""} gap-4`} data-testid="pricing-grid">
            {plans.map((plan) => {
              const isPopular = plan.id === popularId;
              const isBestValue = plan.id === bestValueId && plan.id !== popularId;
              const perClassRate = plan.monthly_price / plan.classes_per_month;
              return (
                <Card key={plan.id}
                  className={`border-2 transition-all hover:shadow-lg cursor-pointer overflow-hidden ${
                    isPopular ? "border-teal-500 shadow-lg ring-2 ring-teal-200" :
                    isBestValue ? "border-emerald-400 shadow-md" :
                    "border-slate-200 hover:border-teal-300"
                  }`}
                  data-testid={`plan-${plan.id}`}
                >
                  {isPopular && (
                    <div className="bg-teal-500 text-white text-xs font-bold text-center py-1.5 uppercase tracking-wide">
                      {t.popular}
                    </div>
                  )}
                  {isBestValue && (
                    <div className="bg-emerald-500 text-white text-xs font-bold text-center py-1.5 uppercase tracking-wide flex items-center justify-center gap-1">
                      <Zap className="w-3 h-3" /> {t.bestValue}
                    </div>
                  )}
                  <CardContent className="p-6 text-center space-y-4">
                    <div>
                      <h3 className="text-lg font-bold text-slate-800 leading-tight" data-testid={`plan-${plan.id}-name`}>{plan.name}</h3>
                      {plan.description && (
                        <p className="text-xs text-slate-400 mt-1 italic">{plan.description}</p>
                      )}
                    </div>
                    <div className="py-3 border-y border-slate-100 space-y-1">
                      <div>
                        <span className="text-3xl font-bold text-teal-700" data-testid={`plan-${plan.id}-price`}>
                          {plan.monthly_price.toLocaleString()} {plan.currency_symbol || currencySymbol}
                        </span>
                        <p className="text-xs text-slate-500 mt-0.5">{t.perMonth}</p>
                      </div>
                      <p className="text-[11px] text-slate-400">≈ {perClassRate.toFixed(0)} {plan.currency_symbol || currencySymbol}/class</p>
                    </div>
                    <div className="space-y-1.5 text-sm">
                      <p className="flex items-center justify-center gap-1.5 text-slate-700">
                        <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                        <strong>{plan.classes_per_month}</strong> {t.classesPerMonth}
                      </p>
                      {plan.classes_per_week > 0 && (
                        <p className="flex items-center justify-center gap-1.5 text-slate-500 text-xs">
                          <CalendarCheck className="w-3.5 h-3.5 text-teal-500" />
                          {plan.classes_per_week} {t.classesPerWeek}
                        </p>
                      )}
                    </div>

                    {paymongoEnabled ? (
                      <Button
                        onClick={() => handlePaymongoCheckout(plan)}
                        disabled={loading}
                        className={`w-full font-semibold text-white ${
                          isPopular ? "bg-teal-600 hover:bg-teal-700" : "bg-emerald-600 hover:bg-emerald-700"
                        }`}
                        data-testid={`buy-paymongo-${plan.id}`}
                        title={t.paymongoHint}
                      >
                        {loading && selectedPlanId === plan.id && provider === "paymongo" ? <span>{t.processing}</span> : (
                          <><CreditCard className="w-4 h-4 mr-2" /> <span>{t.selectPlan}</span></>
                        )}
                      </Button>
                    ) : (
                      <Button
                        onClick={() => handleCheckout(plan)}
                        disabled={loading}
                        className={`w-full font-semibold ${
                          isPopular ? "bg-teal-600 hover:bg-teal-700" :
                          isBestValue ? "bg-emerald-600 hover:bg-emerald-700" :
                          "bg-slate-700 hover:bg-slate-800"
                        }`}
                        data-testid={`buy-${plan.id}`}
                      >
                        {loading && selectedPlanId === plan.id && provider === "stripe" ? <span>{t.processing}</span> : (
                          <><CreditCard className="w-4 h-4 mr-2" /> <span>{t.selectPlan}</span></>
                        )}
                      </Button>
                    )}

                    {paymongoEnabled && fxRate && (
                      <p className="text-[11px] text-slate-500 mt-1.5" data-testid={`paymongo-php-hint-${plan.id}`}>
                        ≈ ₱{(plan.monthly_price * fxRate).toFixed(2)} via PayMongo<br/>
                        <span className="text-slate-400">Card · GCash · Maya · GrabPay</span>
                      </p>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        <p className="text-center text-xs text-slate-400 flex items-center justify-center gap-1.5">
          <CheckCircle2 className="w-3.5 h-3.5 text-green-500" /> {t.secure}
        </p>
      </div>
    </div>
  );
}
